/**
 * BulkToolbar — the selection actions that take over the rows card's header
 * while rows are selected (Figma, "bulk send flow overview" frame): "Bulk
 * send (N)" (on With brand: "Move to brand dashboard (N)", with no Bulk
 * send, Find contacts or Remove), "Export CSV", "Remove" (ruling 49 — never a hard delete on the
 * BE) and "Clear". Read-only staff still see the selection count and Clear
 * so they can back out of an accidental selection, but never Bulk send or
 * Remove — Export CSV stays visible either way, since it never mutates
 * anything (same treatment as View profile/Open thread/Export sheet
 * elsewhere in Outreach).
 *
 * Export CSV has no BE or FE implementation yet — rendered disabled with a
 * "later phase" title, matching the header's disabled Pull creators button
 * and the row menu's disabled Export sheet item rather than being omitted.
 */
import { Button } from '../../../components/ui/button';
import { RETRY_BUSY_TITLE } from './retryLabels';
import { ArrowClockwise, ArrowLineUp, DownloadSimple, MagnifyingGlass, PaperPlaneTilt, Trash } from '../../../design-system/icons';

export interface BulkMove {
  count: number;
  onMove: () => void;
  disabledReason?: string;
}

export interface BulkToolbarProps {
  selectedCount: number;
  /** Selected creators eligible for a first touch; defaults to the selection size (A5). */
  sendableCount?: number;
  canWrite: boolean;
  onSendFirstTouch: () => void;
  onRemove: () => void;
  onClear: () => void;
  /** When set, Bulk send renders disabled with this title. */
  sendDisabledReason?: string;
  /** Why fewer creators are sendable than selected, when some still are. */
  sendNote?: string;
  /** Selected creators without an email (R-6); the button hides at 0. */
  findContactsCount?: number;
  onFindContacts?: () => void;
  /** A find-contacts request or its operation is still running. */
  findContactsBusy?: boolean;
  /** With brand tab (D1): replaces Bulk send, Find contacts and Remove. */
  moveToBrand?: BulkMove;
  /** U6: selected rows whose last send can be retried; the button hides at 0. */
  retryCount?: number;
  onRetry?: () => void;
  retryBusy?: boolean;
  /** Why fewer of the selection enter the bulk Retry batch than are
   *  retry-eligible (the owner's ruling: state the split before the click). */
  retryNote?: string;
}

const SEND_NOTE_ID = 'bulk-send-note';
const SEND_REASON_ID = 'bulk-send-reason';
const RETRY_NOTE_ID = 'bulk-retry-note';

export function BulkToolbar({
  selectedCount,
  sendableCount,
  canWrite,
  onSendFirstTouch,
  onRemove,
  onClear,
  sendDisabledReason,
  sendNote,
  findContactsCount,
  onFindContacts,
  findContactsBusy,
  moveToBrand,
  retryCount,
  onRetry,
  retryBusy,
  retryNote,
}: BulkToolbarProps) {
  if (selectedCount === 0) return null;
  const countLabel = `${selectedCount} ${selectedCount === 1 ? 'creator' : 'creators'}`;
  return (
    <div role="toolbar" aria-label={countLabel} className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-medium text-foreground">{countLabel}</span>
      <span aria-hidden className="h-5 w-px bg-border" />
      {canWrite && !moveToBrand && (
        <Button
          type="button"
          size="sm"
          className="rounded-full"
          onClick={onSendFirstTouch}
          disabled={!!sendDisabledReason}
          title={sendDisabledReason}
          aria-describedby={sendDisabledReason ? SEND_REASON_ID : sendNote ? SEND_NOTE_ID : undefined}
        >
          <PaperPlaneTilt size={14} />
          {`Bulk send (${sendableCount ?? selectedCount})`}
        </Button>
      )}
      {canWrite && !moveToBrand && sendNote && !sendDisabledReason && <span id={SEND_NOTE_ID} className="text-xs text-muted-foreground">{sendNote}</span>}
      {canWrite && !moveToBrand && sendDisabledReason && <span id={SEND_REASON_ID} className="text-xs text-muted-foreground">{sendDisabledReason}</span>}
      {canWrite && !moveToBrand && onFindContacts && (findContactsCount ?? 0) > 0 && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onFindContacts}
          disabled={findContactsBusy}
          title={
            findContactsBusy
              ? 'A contact search is already running'
              : (findContactsCount ?? 0) < selectedCount
                ? `Only the ${findContactsCount} without an email are searched`
                : undefined
          }
        >
          <MagnifyingGlass size={14} />
          {`Find contacts (${findContactsCount})`}
        </Button>
      )}
      {canWrite && moveToBrand && (
        <Button
          type="button"
          size="sm"
          className="rounded-full"
          onClick={moveToBrand.onMove}
          disabled={!!moveToBrand.disabledReason}
          title={moveToBrand.disabledReason}
        >
          <ArrowLineUp size={14} />
          {`Move to brand dashboard (${moveToBrand.count})`}
        </Button>
      )}
      {canWrite && onRetry && (retryCount ?? 0) > 0 && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRetry}
          disabled={retryBusy}
          title={retryBusy ? RETRY_BUSY_TITLE : undefined}
          aria-describedby={retryNote ? RETRY_NOTE_ID : undefined}
        >
          <ArrowClockwise size={14} />
          {`Send now / Retry (${retryCount})`}
        </Button>
      )}
      {canWrite && onRetry && (retryCount ?? 0) > 0 && retryNote && (
        <span id={RETRY_NOTE_ID} className="text-xs text-muted-foreground">
          {retryNote}
        </span>
      )}
      <Button type="button" size="sm" variant="outline" disabled title="Export CSV — later phase">
        <DownloadSimple size={14} />
        Export CSV
      </Button>
      {canWrite && !moveToBrand && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={onRemove}
        >
          <Trash size={14} />
          Remove
        </Button>
      )}
      <Button type="button" size="sm" variant="ghost" className="text-muted-foreground" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
